import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import '../styles/Table.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
const TABLE_DEFAULT = { rows: [['', ''], ['', '']] };

function LoginPrompt({ onLogin }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/auth/login`, { password });
      localStorage.setItem('token', res.data.token);
      onLogin(res.data.token);
    } catch { setError('密码错误'); }
  };
  return (
    <div className="table-login">
      <form onSubmit={submit}>
        <h2>Table</h2>
        <input type="password" placeholder="密码" value={password}
          onChange={e => setPassword(e.target.value)} autoFocus />
        {error && <p className="table-login-error">{error}</p>}
        <button type="submit">进入</button>
      </form>
    </div>
  );
}

// Real <table>/<td>s, not swapped-in <input>s, on purpose: plain cells give
// two things for free that a custom editor would have to rebuild by hand —
// the browser positions the caret correctly on click (no caretRangeFromPoint
// juggling, see the Sticker page for how much that costs to get right), and
// dragging a selection across multiple cells and copying it produces
// tab-separated text that Excel/Sheets understand natively on paste.
// contentEditable on the <td> itself means there's no separate "read" vs
// "edit" element to swap between, so neither of those breaks while editing.
function TableBoard({ token, onExpired }) {
  // Starts as a real 2x2 grid, never null: gating the whole page render on
  // "has the fetch resolved yet" is what turned an expired/invalid token
  // (a normal, expected thing — 7-day JWT) into a completely blank page
  // with no toolbar, no error, nothing to act on. The toolbar and grid
  // structure should always be there; only the cell *contents* depend on
  // the fetch succeeding.
  const [rows, setRows] = useState(TABLE_DEFAULT.rows);
  const [loadError, setLoadError] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/table`, { headers });
      setRows(data.rows);
      setLoadError(false);
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem('token'); onExpired(); return; }
      setLoadError(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = (next) => {
    setRows(next);
    axios.put(`${API}/table`, { rows: next }, { headers }).catch(e => {
      if (e.response?.status === 401) { localStorage.removeItem('token'); onExpired(); return; }
      setLoadError(true);
    });
  };

  const setCell = (r, c, text) => {
    const next = rows.map(row => [...row]);
    next[r][c] = text;
    save(next);
  };

  const addRow = () => {
    const cols = rows[0]?.length || 1;
    save([...rows, Array(cols).fill('')]);
  };

  const deleteRow = (r) => {
    if (rows.length <= 1) return;
    save(rows.filter((_, i) => i !== r));
  };

  const addColumn = () => {
    save(rows.map(row => [...row, '']));
  };

  const deleteColumn = (c) => {
    if ((rows[0]?.length || 0) <= 1) return;
    save(rows.map(row => row.filter((_, i) => i !== c)));
  };

  // Each cell is its own contentEditable region (needed so a click lands
  // the caret correctly and edits stay scoped to that one cell) — but that
  // also means a mouse drag can't select text across a cell boundary the
  // way it can in a plain, non-editable table. Rather than build real
  // spreadsheet-style Tab/Enter cell navigation to work around that, copy
  // the whole table as tab-separated text directly from the data Excel/
  // Sheets both understand a pasted TSV block natively.
  const copyTableAsTsv = async () => {
    const tsv = rows.map(row => row.join('\t')).join('\n');
    try { await navigator.clipboard.writeText(tsv); } catch {}
  };

  const colCount = rows[0]?.length || 0;

  return (
    <div className="table-page">
      <div className="table-toolbar">
        {loadError && (
          <span className="table-error">加载失败，改动可能没保存到服务器——
            <button className="table-retry" onClick={load}>重试</button>
          </span>
        )}
        <button className="btn btn-small" onClick={addRow}>+ 行</button>
        <button className="btn btn-small" onClick={addColumn}>+ 列</button>
        <button className="btn btn-small" onClick={copyTableAsTsv}>⧉ 复制整表</button>
        <span className="table-hint">选中单个格子也能直接 Cmd/Ctrl+C；整表贴进 Excel 用左边的按钮</span>
      </div>
      <div className="table-wrap">
        <table className="plain-table">
          <thead>
            <tr>
              {Array.from({ length: colCount }).map((_, c) => (
                <th key={c}>
                  <button className="table-col-del" title="删除这一列" onClick={() => deleteColumn(c)}>×</button>
                </th>
              ))}
              <th className="table-row-del-col" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => (
              <tr key={r}>
                {row.map((cell, c) => (
                  <td key={c} contentEditable suppressContentEditableWarning
                    onBlur={e => setCell(r, c, e.currentTarget.textContent)}>
                    {cell}
                  </td>
                ))}
                <td className="table-row-del-col">
                  <button className="table-row-del" title="删除这一行" onClick={() => deleteRow(r)}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Table() {
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  if (!token) return <LoginPrompt onLogin={setToken} />;
  return <TableBoard token={token} onExpired={() => setToken(null)} />;
}
